import { useEffect, useState } from "react";
import { AlertTriangleIcon, CheckSquare, PlusIcon, XIcon } from "lucide-react";
import { Input } from "@component/input";
import { Checkbox } from "@component/checkbox";
import { Certificate } from "@type/cv";
import { Button } from "@component/button";
import { cn } from "@util/tailwind";

interface Props {
  isEditing: boolean;
  isDisabled: boolean;
  data: Certificate[];
  onChange: (values: Certificate[]) => void;
  onEdit: () => void
}

export function Certifications(props: Props) {
  const [certifications, setCertifications] = useState<Certificate[]>([]);

  useEffect(() => {
    setCertifications(props.data.map((cert) => {
      if (cert.expiration_date && cert.expiration_date !== "") {
        return {
          ...cert,
          is_checked_expired_date: true
        }
      } else {
        return cert
      }
    }));
  }, []);

  useEffect(() => {
    props.onChange(certifications);
  }, [certifications]);

  const handleCertificationChange = (
    index: number,
    field: keyof Certificate,
    value: string
  ) => {
    const updatedCertifications = [...certifications];
    if (field === "is_checked_expired_date") {
      updatedCertifications[index].is_checked_expired_date = value === "true";
    } else {
      updatedCertifications[index][field] = value;
    }
    setCertifications(updatedCertifications);
  };

  const addCertification = () => {
    setCertifications([
      ...certifications,
      {
        name: "",
        institution: "",
        date: "",
        is_checked_expired_date: false,
        expiration_date: ""
      },
    ]);
  };

  const removeCertification = (index: number) => {
    setCertifications(certifications.filter((_, i) => i !== index));
  };

  const toggleExpiry = (index: number) => {
    const updatedCertifications = [...certifications];

    updatedCertifications[index].is_checked_expired_date = !updatedCertifications[index].is_checked_expired_date;
    if (
      updatedCertifications[index]?.expiration_date ||
      updatedCertifications[index]?.expiration_date === ""
    ) {
      updatedCertifications[index].expiration_date = undefined;
    } else {
      updatedCertifications[index].expiration_date = "";
    }
    setCertifications(updatedCertifications);
  };

  return (
    <div className="p-6 mb-10">
      <div>
        {!props.isEditing && certifications.length === 0 && (
          <Button
            onClick={props.onEdit}
            disabled={props.isDisabled}
            variant="ghost"
            className="flex items-center text-blue-500 hover:text-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Certification
          </Button>
        )}
        {certifications.map((cert, index) => (
          <div key={index}>
            {props.isEditing ? (
              <div className="w-full space-y-4 mb-8">
                <button
                  onClick={() => removeCertification(index)}
                  className="float-right text-red-500 hover:text-red-700"
                >
                  <XIcon className="h-5 w-5" />
                </button>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-black mb-1 block">
                      Certification Title
                    </label>
                    <Input
                      value={cert.name}
                      onChange={(e) =>
                        handleCertificationChange(index, "name", e.target.value)
                      }
                      className="border-gray-300 text-black"
                    />
                    {cert.name === "" && (
                      <div className="flex flex-row items-center mt-2 space-x-1">
                        <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                        <p className="text-red-600 text-sm">
                          Certification title is required.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      className="text-sm text-black mb-1 block"
                    >
                      Issuer
                    </label>
                    <Input
                      value={cert.institution}
                      onChange={(e) =>
                        handleCertificationChange(
                          index,
                          "institution",
                          e.target.value
                        )
                      }
                      className="border-gray-300 text-black"
                    />
                    {cert.institution === "" && (
                      <div className="flex flex-row items-center mt-2 space-x-1">
                        <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                        <p className="text-red-600 text-sm">
                          Issuer is required.
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-black mb-1 block">
                      Date
                    </label>
                    <Input
                      value={cert.date}
                      onChange={(e) =>
                        handleCertificationChange(index, "date", e.target.value)
                      }
                      className="border-gray-300 text-black"
                    />
                    {cert.date === "" && (
                      <div className="flex flex-row items-center mt-2 space-x-1">
                        <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                        <p className="text-red-600 text-sm">Date is required.</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`has-expiry-${index}`}
                    checked={cert.is_checked_expired_date}
                    onCheckedChange={() => toggleExpiry(index)}
                  />

                  <label
                    htmlFor={`has-expiry-${index}`}
                    className="text-sm text-black select-none"
                  >
                    Has expiration date
                  </label>
                </div>
                {cert.is_checked_expired_date && (
                  <div className="w-40">
                    <label className="text-sm text-black mb-1 block">
                      Expiry
                    </label>
                    <Input
                      value={cert.expiration_date}
                      onChange={(e) =>
                        handleCertificationChange(
                          index,
                          "expiration_date",
                          e.target.value
                        )
                      }
                      className="border-gray-300 text-black"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div
                key={index}
                className={cn("relative pl-12", {
                  "pb-8": index < certifications.length - 1,
                })}
              >
                {/* Timeline line - only show for non-last items */}
                {index < (certifications || []).length - 1 && (
                  <div
                    className="absolute left-3 top-1 bottom-0 w-[2px]"
                    style={{ backgroundColor: "#fce7f3" }}
                  ></div>
                )}

                {/* Icon */}
                <div className="absolute left-0 top-1">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center z-10 border-2"
                    style={{
                      backgroundColor: "#fce7f3",
                      border: "0.909091px solid #db2777",
                    }}
                  >
                    <CheckSquare
                      className="h-3 w-3"
                      style={{ color: "#db2777" }}
                    />
                  </div>
                </div>

                {/* Content */}
                <div>
                  <div className="flex sm:flex-row flex-col flex-wrap items-start gap-2 mb-1 relative">
                    <h5 className="font-medium text-gray-900 dark:text-gray-100 w-full text-left">
                      {cert.name}
                    </h5>
                    {cert.date && (
                      <span className="text-sm text-gray-500 dark:text-gray-400 sm:absolute sm:right-0 sm:top-0">
                        {cert.date}
                        {!!cert.expiration_date &&
                          ` (Expires: ${cert.expiration_date})`}
                      </span>
                    )}

                  </div>
                  {cert.institution && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      {cert.institution}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {props.isEditing && (
        <Button
          onClick={addCertification}
          variant="ghost"
          className="mt-4 flex items-center text-blue-500 hover:text-blue-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Certification
        </Button>
      )}
    </div>
  );
}
